"use client";

import { Button } from "@workspace/ui/components/button";
import Image from "next/image";
import Link from "next/link";
import posthog from "posthog-js";
import { useConfig } from "@/components/providers/config";
import { normalizeUrl } from "@/lib/utils";

export default function LogoCloud() {
  const { config } = useConfig();

  // Extract CTA fields with sensible fallbacks
  // biome-ignore lint/suspicious/noExplicitAny: Storyblok config is dynamic
  const cta: any = (config as any)?.cta ?? {};
  const eyebrow: string = cta.eyebrow ?? "Ready to achieve your goals?";
  const title: string = cta.title ?? "Schedule a meeting with us today";
  const description: string =
    cta.description ?? "We're here to help you grow your talent.";
  // Extract link from config (prefer 'link' from new Config type, then fallback to 'call_to_action_link' for backwards compatibility, then cta.link)
  const link =
    (config as Record<string, unknown>)?.link ??
    (config as Record<string, unknown>)?.call_to_action_link ??
    cta.link ??
    null;
  const href: string =
    (link as { cached_url?: string; url?: string })?.cached_url ??
    (link as { cached_url?: string; url?: string })?.url ??
    cta.href ??
    "/contact";
  const buttonLabel: string =
    (link as { title?: string })?.title ??
    (typeof (config as Record<string, unknown>)?.call_to_action_label ===
    "string"
      ? ((config as Record<string, unknown>).call_to_action_label as string)
      : undefined) ??
    "Schedule A Meeting";
  const configImage = (config as Record<string, unknown>)?.image;
  const imageSrc: string =
    (configImage &&
    typeof configImage === "object" &&
    configImage !== null &&
    "filename" in configImage
      ? (configImage as { filename?: string }).filename
      : undefined) ??
    cta.image?.filename ??
    cta.image?.url ??
    (typeof cta.image === "string" ? (cta.image as string) : undefined) ??
    "/images/portrait-2.jpg";

  const handleCtaClick = () => {
    posthog.capture("cta_clicked", {
      cta_title: title,
      cta_label: buttonLabel,
      cta_href: normalizeUrl(href),
      cta_location: "call_to_action_section",
    });
  };

  return (
    <div className="relative">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 relative isolate py-24">
        <div className="bg-violate-950 rounded-xl shadow-2xl overflow-hidden px-12 py-24 ring-2 ring-primary relative isolate hover:scale-102 transition-all duration-300">
          <Image
            src={imageSrc}
            alt="Call to Action"
            fill
            className="object-cover object-center absolute inset-0 grayscale opacity-25"
          />
          <Link
            href={normalizeUrl(href)}
            className="absolute inset-0 z-20"
            onClick={handleCtaClick}
          />
          <div className="flex flex-col space-y-4 max-w-screen-lg sm:text-center mx-auto relative z-10">
            <span className="text-teal-500 text-2xl drop-shadow-2xl font-medium font-display uppercase">
              {eyebrow}
            </span>
            <h2 className="text-5xl md:text-6xl drop-shadow-2xl font-extrabold font-display bg-gradient-to-br from-violet-100 via-zinc-50 to-violet-200 bg-clip-text text-transparent leading-tight pb-1">
              {title}
            </h2>
            <p className="text-xl drop-shadow-2xl max-w-prose font-medium text-violet-200 mt-4 mx-auto mb-8">
              {description}
            </p>
            <Button size="lg" className="sm:w-fit sm:mx-auto" asChild>
              <Link
                href={normalizeUrl(href)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleCtaClick}
              >
                {buttonLabel}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
